import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { HaynesProVesaComponentModel } from '../typings/haynes-pro.vesa';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-comfort-wiring-diagram',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-comfort-wiring-diagram.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-comfort-wiring-diagram.component.html')
})
export class HaynesProDiagnosticToolVesaComfortWiringDiagramComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() allComponents : Array<HaynesProVesaComponentModel> = [];
  @Input() component     : HaynesProVesaComponentModel;
  @Input() schemeUrl     : string;

  @ViewChild('zoomViewport', { static: false }) zoomViewport : ElementRef<HTMLDivElement>;
  @ViewChild('zoomStage',    { static: false }) zoomStage    : ElementRef<HTMLDivElement>;
  @ViewChild('svgContainer', { static: false }) svgContainer : ElementRef<HTMLDivElement>;

  hoveredCode     = '';
  isDragging      = false;
  isLoading       = false;
  isMonochrome    = false;
  loadError       = '';
  panX            = 0;
  panY            = 0;
  selectedCode    = '';
  showColourCodes = false;
  zoomLevel       = 1;

  readonly WIRE_COLOUR_CODES : ReadonlyArray<{ code : string; name : string }> = [
    { code: 'BE', name: 'Beige'        },
    { code: 'BK', name: 'Black'        },
    { code: 'BN', name: 'Brown'        },
    { code: 'BU', name: 'Blue'         },
    { code: 'DB', name: 'Dark Brown'   },
    { code: 'DG', name: 'Dark Green'   },
    { code: 'DO', name: 'Dark Orange'  },
    { code: 'DR', name: 'Dark Red'     },
    { code: 'GN', name: 'Green'        },
    { code: 'GY', name: 'Grey'         },
    { code: 'LB', name: 'Light Blue'   },
    { code: 'LG', name: 'Light Green'  },
    { code: 'LV', name: 'Light Violet' },
    { code: 'OG', name: 'Orange'       },
    { code: 'PK', name: 'Pink'         },
    { code: 'RD', name: 'Red'          },
    { code: 'TN', name: 'Tan'          },
    { code: 'TR', name: 'Transparent'  },
    { code: 'VT', name: 'Violet'       },
    { code: 'WH', name: 'White'        },
    { code: 'YE', name: 'Yellow'       }
  ];

  private componentElements   : Map<string, SVGGraphicsElement>                                          = new Map();
  private componentBoxes      : Map<string, { x : number; y : number; width : number; height : number }> = new Map();
  private hitAreaGroup        : SVGGElement     | null                                                   = null;
  private currentSchemeUrl                                                                               = '';
  private highlightGroup      : SVGGElement     | null                                                   = null;
  private hoverHighlightGroup : SVGGElement     | null                                                   = null;
  private svgRoot             : SVGSVGElement   | null                                                   = null;
  private viewInitialized                                                                                = false;

  private dragMouseDownBound ?: ( event : MouseEvent ) => void;
  private dragMouseMoveBound ?: ( event : MouseEvent ) => void;
  private dragMouseUpBound   ?: ( event : MouseEvent ) => void;
  private wheelListenerBound ?: ( event : WheelEvent ) => void;

  private dragLatestX   = 0;
  private dragLatestY   = 0;
  private dragRafId     : number | null = null;
  private dragStartX    = 0;
  private dragStartY    = 0;
  private dragStartPanX = 0;
  private dragStartPanY = 0;

  private highlightDelayTimer : number | null = null;
  private highlightRafId      : number | null = null;

  private boundSvgTargets    : Array<SVGGraphicsElement> = [];
  private leaveDebounceTimer : number | null             = null;

  private readonly DRAG_THRESHOLD             = 5;
  private readonly HIGHLIGHT_FOLLOW_MS        = 300;
  private readonly HIGHLIGHT_GLIDE_MS         = 320;
  private readonly HOVER_LEAVE_DELAY_MS       = 60;
  private readonly SVG_NS                     = 'http://www.w3.org/2000/svg';
  private readonly ZOOM_STEPS : Array<number> = [
    0.5, 0.75, 1, 1.25, 1.5, 1.75,
    2,   2.25, 2.5, 2.75, 3,  3.5,
    4,   4.5,  5
  ];

  constructor ( private zone : NgZone ) {}

  ngAfterViewInit () : void {
    this.viewInitialized = true;

    this.zone.runOutsideAngular(() => {
      this.bindWheelZoom();
      this.bindDragPan();
    });

    if (this.schemeUrl && this.schemeUrl !== this.currentSchemeUrl) {
      this.loadSvg();
    } else if (this.component && this.svgRoot) {
      this.selectedCode = this.component.id || '';
      this.focusComponent(this.selectedCode, false);
    }
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.schemeUrl && this.viewInitialized && this.schemeUrl !== this.currentSchemeUrl) {
      this.loadSvg();
      return;
    }

    if (changes.component && this.component && this.svgRoot) {
      const newCode = this.component.id || '';

      if (newCode && newCode !== this.selectedCode) {
        this.selectedCode = newCode;
        this.focusComponent(this.selectedCode, true);
      }
    }
  }

  ngOnDestroy () : void {
    this.detachSvgHandlers();
    this.unbindDragPan();
    this.unbindWheelZoom();
    this.clearHighlightDelayTimer();
    this.clearLeaveDebounceTimer();
    this.cancelHighlightAnimation();
    this.componentElements.clear();
    this.componentBoxes.clear();

    this.svgRoot             = null;
    this.highlightGroup      = null;
    this.hoverHighlightGroup = null;
  }

  // ── Transform getter + view reset

  get stageTransform () : string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
  }

  isHighlighted ( item : HaynesProVesaComponentModel ) : boolean {
    return !!item?.id && item.id === this.hoveredCode;
  }

  isSelected ( item : HaynesProVesaComponentModel ) : boolean {
    return !!item?.id && item.id === this.selectedCode;
  }

  onRowClick ( item : HaynesProVesaComponentModel ) : void {
    const code = item?.id;

    if (!code || code === this.selectedCode) {
      return;
    }

    this.selectedCode = code;
    this.focusComponent(code, true);
  }

  onRowEnter ( item : HaynesProVesaComponentModel ) : void {
    this.hoveredCode = item?.id || '';
    this.applyHoverClass();
  }

  onRowLeave () : void {
    this.hoveredCode = '';
    this.applyHoverClass();
  }

  trackByComponent ( _index : number, item : HaynesProVesaComponentModel ) : string {
    return item?.id || item?.name;
  }

  resetView () : void {
    // Cancel any in-flight glide so the manual rAF loop can't overwrite thereset animation mid-flight.
    this.cancelHighlightAnimation();
    this.clearHighlightDelayTimer();

    // Close any open popover so reset is a true clean slate.
    this.showColourCodes = false;
    this.panX            = 0;
    this.panY            = 0;
    this.zoomLevel       = 1;
    this.updateStageTransform(true);

    this.selectedCode = this.component?.id || '';

    if (this.selectedCode) {
      const rootBox = this.componentBoxes.get(this.selectedCode);

      if (rootBox) {
        this.positionOverlayAtRoot(this.highlightGroup, rootBox, false);
      } else if (this.highlightGroup) {
        this.highlightGroup.style.opacity = '0';
      }
    } else if (this.highlightGroup) {
      this.highlightGroup.style.opacity = '0';
    }

    this.applyHoverClass();
  }

  toggleMonochrome () : void {
    this.isMonochrome = !this.isMonochrome;

    if (this.svgRoot) {
      this.svgRoot.classList.toggle('cwd-monochrome', this.isMonochrome);
    }
  }

  toggleColourCodes ( event : MouseEvent ) : void {
    event.stopPropagation();
    this.showColourCodes = !this.showColourCodes;
  }

  @HostListener('document:click', [ '$event' ])
  onDocumentClickForColourCodes ( event : MouseEvent ) : void {
    if (!this.showColourCodes) {
      return;
    }

    const target = event.target as Element | null;
    if (target && target.closest('.vesa-comfort-wd-codes-popover')) {
      return;
    }

    this.showColourCodes = false;
  }

  // ── SVG loading ──

  private loadSvg () : void {
    if (!this.schemeUrl || !this.svgContainer?.nativeElement) {
      return;
    }

    this.currentSchemeUrl = this.schemeUrl;
    this.isLoading        = true;
    this.loadError        = '';

    fetch(this.schemeUrl, { mode: 'cors' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(svgText => {
        this.injectInlineSvg(svgText);
        this.createHighlightOverlay();

        requestAnimationFrame(() => {
          this.buildComponentElementMap();
          this.classifyWireElements();
          this.attachSvgHandlers();

          this.panX         = 0;
          this.panY         = 0;
          this.zoomLevel    = 1;
          this.selectedCode = this.component?.id || '';

          this.updateStageTransform(false);

          // Landing view shows the WHOLE diagram fitted edge-to-edge
          if (this.selectedCode) {
            const rootBox = this.componentBoxes.get(this.selectedCode);

            if (rootBox) {
              this.positionOverlayAtRoot(this.highlightGroup, rootBox, false);
            }
          }
        });
      })
      .catch(err => {
        this.loadError = err?.message || 'Unable to load wiring diagram';

        if (this.svgContainer?.nativeElement) {
          this.svgContainer.nativeElement.innerHTML =
            `<img class="cwd-fallback-image" src="${this.schemeUrl}" alt="" />`;
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private injectInlineSvg ( svgText : string ) : void {
    if (!this.svgContainer?.nativeElement) {
      return;
    }

    const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svg    = parsed.documentElement as unknown as SVGSVGElement;

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.style.width   = '100%';
    svg.style.height  = '100%';
    svg.style.display = 'block';
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Wrap all original children in a content layer so the monochrome filter can target ONLY the diagram content
    const contentLayer = document.createElementNS(this.SVG_NS, 'g');
    contentLayer.setAttribute('class', 'cwd-content-layer');
    while (svg.firstChild) {
      contentLayer.appendChild(svg.firstChild);
    }
    svg.appendChild(contentLayer);

    if (this.isMonochrome) {
      svg.classList.add('cwd-monochrome');
    }

    this.svgContainer.nativeElement.innerHTML = '';
    this.svgContainer.nativeElement.appendChild(svg);

    this.svgRoot = svg;
  }

  // ── Component element lookup ──

  private buildComponentElementMap () : void {
    this.componentElements.clear();
    this.componentBoxes.clear();

    if (!this.svgRoot) {
      return;
    }

    ( this.allComponents || [] ).forEach(item => {
      const code = item?.id;

      if (!code) {
        return;
      }

      const element = this.findComponentElement(code);

      if (!element) {
        return;
      }

      this.componentElements.set(code, element);

      const unionBox = this.computeLogicalBBox(element, code);

      if (unionBox) {
        this.componentBoxes.set(code, unionBox);
      }
    });
  }

  private computeLogicalBBox ( element : SVGGraphicsElement, code : string ) : { x : number; y : number; width : number; height : number } | null {
    if (!this.svgRoot) {
      return null;
    }

    const labels = Array.from(this.svgRoot.querySelectorAll('text'))
      .filter(t => ( t.textContent || '' ).trim() === code);

    if (!labels.length) {
      return this.getBBoxInRootSpace(element);
    }

    // Iterate until we find a label with a real bbox
    let anchorBox : { x : number; y : number; width : number; height : number } | null = null;

    for (const label of labels) {
      const box = this.getBBoxInRootSpace(label as unknown as SVGGraphicsElement);

      if (box && box.width && box.height) {
        anchorBox = box;
        break;
      }
    }

    if (!anchorBox) {
      return this.getBBoxInRootSpace(element);
    }

    // Search radius scales with the SVG's smaller dimension
    const svgRootBox = this.getBBoxSafe(this.svgRoot);
    const svgDim     = svgRootBox && svgRootBox.width && svgRootBox.height
      ? Math.min(svgRootBox.width, svgRootBox.height)
      : 1000;
    const searchRadius = svgDim * 0.06;

    const zoneMinX = anchorBox.x - searchRadius;
    const zoneMinY = anchorBox.y - searchRadius;
    const zoneMaxX = anchorBox.x + anchorBox.width  + searchRadius;
    const zoneMaxY = anchorBox.y + anchorBox.height + searchRadius;

    let minX = anchorBox.x;
    let minY = anchorBox.y;
    let maxX = anchorBox.x + anchorBox.width;
    let maxY = anchorBox.y + anchorBox.height;

    const anchorMaxDim = Math.max(anchorBox.width, anchorBox.height);
    const sizeCap      = anchorMaxDim * 8;

    const candidates = Array.from(this.svgRoot.querySelectorAll('text, rect, circle, path'));

    for (const c of candidates) {
      // Open paths are treated as wires — exclude so a wire passing nearby doesn't expand the bbox.
      if (c.tagName.toLowerCase() === 'path') {
        const d = c.getAttribute('d') || '';
        if (!/[zZ]\s*$/.test(d.trim())) {
          continue;
        }
      }

      const cBox = this.getBBoxInRootSpace(c as SVGGraphicsElement);

      if (!cBox || !cBox.width || !cBox.height) {
        continue;
      }

      if (cBox.width > sizeCap || cBox.height > sizeCap) {
        continue;
      }

      const cx = cBox.x + cBox.width  / 2;
      const cy = cBox.y + cBox.height / 2;

      if (cx < zoneMinX || cx > zoneMaxX || cy < zoneMinY || cy > zoneMaxY) {
        continue;
      }

      minX = Math.min(minX, cBox.x);
      minY = Math.min(minY, cBox.y);
      maxX = Math.max(maxX, cBox.x + cBox.width);
      maxY = Math.max(maxY, cBox.y + cBox.height);
    }

    const cappedHalf = ( svgDim * 0.15 ) / 2;
    const acx        = anchorBox.x + anchorBox.width  / 2;
    const acy        = anchorBox.y + anchorBox.height / 2;
    const cappedMinX = acx - cappedHalf;
    const cappedMinY = acy - cappedHalf;
    const cappedMaxX = acx + cappedHalf;
    const cappedMaxY = acy + cappedHalf;

    minX = Math.max(minX, cappedMinX);
    minY = Math.max(minY, cappedMinY);
    maxX = Math.min(maxX, cappedMaxX);
    maxY = Math.min(maxY, cappedMaxY);

    return {
      x     : minX,
      y     : minY,
      width : Math.max(0, maxX - minX),
      height: Math.max(0, maxY - minY)
    };
  }

  private classifyWireElements () : void {
    if (!this.svgRoot) {
      return;
    }

    const candidates = Array.from(this.svgRoot.querySelectorAll('path, line, polyline'));

    for (const el of candidates) {

      if (this.isInsideDefsLikeAncestor(el)) {
        continue;
      }

      if (el.tagName.toLowerCase() === 'path') {
        const d = el.getAttribute('d') || '';
        if (/[zZ]\s*$/.test(d.trim())) {
          continue;
        }
      }

      const computed = window.getComputedStyle(el as Element);
      const stroke   = computed.stroke;

      if (!stroke || stroke === 'none' || stroke === 'transparent') {
        continue;
      }

      if (this.isPureBlack(stroke)) {
        continue;
      }

      // Near-white strokes blend into the page background in colour mode
      if (this.isNearWhite(stroke)) {
        continue;
      }

      const strokeOpacity = parseFloat(computed.strokeOpacity || '1');

      if (isNaN(strokeOpacity) || strokeOpacity < 0.5) {
        continue;
      }

      if (this.isInsideComponentBBox(el as SVGGraphicsElement)) {
        continue;
      }

      el.classList.add('cwd-svg-wire');
    }
  }

  private isInsideDefsLikeAncestor ( el : Element ) : boolean {
    let parent : Element | null = el.parentElement;

    while (parent && parent !== this.svgRoot) {
      const tag = parent.tagName.toLowerCase();

      if (tag === 'defs'
          || tag === 'clippath'
          || tag === 'mask'
          || tag === 'pattern'
          || tag === 'symbol') {
        return true;
      }

      parent = parent.parentElement;
    }

    return false;
  }

  private isNearWhite ( color : string ) : boolean {
    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);

    if (!match) {
      return false;
    }

    return parseInt(match[1], 10) >= 220
        && parseInt(match[2], 10) >= 220
        && parseInt(match[3], 10) >= 220;
  }

  private isInsideComponentBBox ( el : SVGGraphicsElement ) : boolean {
    const box = this.getBBoxInRootSpace(el);

    if (!box || !box.width || !box.height) {
      return false;
    }

    const tolerance = 5; // svg user-space units, sub-pixel rendering slack

    for (const compBox of this.componentBoxes.values()) {
      if (box.x                + tolerance >= compBox.x
          && box.y             + tolerance >= compBox.y
          && box.x + box.width <= compBox.x + compBox.width  + tolerance
          && box.y + box.height<= compBox.y + compBox.height + tolerance) {
        return true;
      }
    }

    return false;
  }

  private isPureBlack ( color : string ) : boolean {
    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);

    if (!match) {
      return false;
    }

    return parseInt(match[1], 10) === 0
        && parseInt(match[2], 10) === 0
        && parseInt(match[3], 10) === 0;
  }

  private findComponentElement ( code : string ) : SVGGraphicsElement | null {
    if (!this.svgRoot || !code) {
      return null;
    }

    const escapedCode = this.cssEscape(code);

    const byIdSelectors = [
      `[id="${code}"]`,
      `[id="ecomponent${code}"]`,
      `[id="component${code}"]`,
      `[id^="ecomponent${code}_"]`,
      `[id^="component${code}_"]`,
      `[id^="${escapedCode}_"]`
    ];

    for (const selector of byIdSelectors) {
      const byId = this.svgRoot.querySelector(selector);

      if (byId) {
        return byId as SVGGraphicsElement;
      }
    }

    const matchingTexts = Array.from(this.svgRoot.querySelectorAll('text'))
      .filter(text => ( text.textContent || '' ).trim() === code);

    if (!matchingTexts.length) {
      return null;
    }

    // Prefer the nearest ancestor <g>
    for (const text of matchingTexts) {
      let parent : Element | null = text.parentElement;

      while (parent && parent !== this.svgRoot) {
        if (parent.tagName.toLowerCase() === 'g') {
          const bbox = this.getBBoxSafe(parent as unknown as SVGGraphicsElement);

          if (bbox && bbox.width > 0 && bbox.height > 0) {
            return parent as unknown as SVGGraphicsElement;
          }
        }

        parent = parent.parentElement;
      }
    }

    // Fallback — find a <rect> whose bbox overlaps the label bbox
    for (const text of matchingTexts) {
      const textBox = this.getBBoxSafe(text as unknown as SVGGraphicsElement);

      if (!textBox) {
        continue;
      }

      const enclosingRect = Array.from(this.svgRoot.querySelectorAll('rect'))
        .find(rect => {
          const rBox = this.getBBoxSafe(rect as unknown as SVGGraphicsElement);
          return rBox && this.bboxesOverlap(rBox, textBox) && rBox.width > textBox.width;
        });

      if (enclosingRect) {
        return enclosingRect as unknown as SVGGraphicsElement;
      }
    }

    return matchingTexts[0] as unknown as SVGGraphicsElement;
  }

  private bboxesOverlap ( a : SVGRect, b : SVGRect ) : boolean {
    return !(
      a.x + a.width  < b.x ||
      b.x + b.width  < a.x ||
      a.y + a.height < b.y ||
      b.y + b.height < a.y
    );
  }

  private cssEscape ( value : string ) : string {
    return ( value || '' ).replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  // ── Highlight overlay ──

  private createHighlightOverlay () : void {
    if (!this.svgRoot) {
      return;
    }

    const group = document.createElementNS(this.SVG_NS, 'g');
    group.setAttribute('class', 'cwd-highlight-group');
    group.setAttribute('pointer-events', 'none');
    group.style.opacity = '0';

    const rect = document.createElementNS(this.SVG_NS, 'rect');
    rect.setAttribute('x', '0');
    rect.setAttribute('y', '0');
    rect.setAttribute('width', '0');
    rect.setAttribute('height', '0');
    rect.setAttribute('rx', '3');
    rect.setAttribute('fill', 'none');
    rect.setAttribute('stroke', '#D0021B');
    rect.setAttribute('stroke-width', '2');
    rect.setAttribute('vector-effect', 'non-scaling-stroke');

    group.appendChild(rect);

    this.svgRoot.appendChild(group);

    this.highlightGroup = group;

    // Hover overlay — parallel to the selection overlay, but styled as a subtle blue outline.
    const hoverGroup = document.createElementNS(this.SVG_NS, 'g');
    hoverGroup.setAttribute('class', 'cwd-hover-highlight-group');
    hoverGroup.setAttribute('pointer-events', 'none');
    hoverGroup.style.opacity = '0';

    const hoverRect = document.createElementNS(this.SVG_NS, 'rect');
    hoverRect.setAttribute('x', '0');
    hoverRect.setAttribute('y', '0');
    hoverRect.setAttribute('width', '0');
    hoverRect.setAttribute('height', '0');
    hoverRect.setAttribute('rx', '3');
    hoverRect.setAttribute('fill', 'none');
    hoverRect.setAttribute('stroke', '#e87027');
    hoverRect.setAttribute('stroke-width', '2');
    hoverRect.setAttribute('vector-effect', 'non-scaling-stroke');

    hoverGroup.appendChild(hoverRect);
    this.svgRoot.appendChild(hoverGroup);

    this.hoverHighlightGroup = hoverGroup;
  }

  // ── Focus / pan-to-component ──

  private focusComponent ( code : string, animate : boolean ) : void {
    if (!this.svgRoot || !code || !this.zoomViewport?.nativeElement) {
      return;
    }

    const element = this.componentElements.get(code);

    if (!element) {
      return;
    }

    const rootBox = this.componentBoxes.get(code) || this.getBBoxInRootSpace(element);

    if (!rootBox) {
      return;
    }

    this.clearHighlightDelayTimer();
    this.applyHoverClass();
    const viewportEl  = this.zoomViewport.nativeElement;
    const viewportBox = viewportEl.getBoundingClientRect();
    const elementBox  = element.getBoundingClientRect();

    if (!elementBox.width || !elementBox.height) {
      return;
    }

    const viewCenterX    = viewportBox.left + viewportBox.width  / 2;
    const viewCenterY    = viewportBox.top  + viewportBox.height / 2;
    const elementCenterX = elementBox.left  + elementBox.width   / 2;
    const elementCenterY = elementBox.top   + elementBox.height  / 2;

    this.panX += ( viewCenterX - elementCenterX );
    this.panY += ( viewCenterY - elementCenterY );

    this.updateStageTransform(animate);

    if (animate) {
      this.highlightDelayTimer = window.setTimeout(() => {
        this.highlightDelayTimer = null;
        this.positionOverlayAtRoot(this.highlightGroup, rootBox, true);
      }, this.HIGHLIGHT_FOLLOW_MS);
    } else {
      this.positionOverlayAtRoot(this.highlightGroup, rootBox, false);
    }
  }

  private clearHighlightDelayTimer () : void {
    if (this.highlightDelayTimer !== null) {
      window.clearTimeout(this.highlightDelayTimer);
      this.highlightDelayTimer = null;
    }
  }

  private getBBoxInRootSpace ( element : SVGGraphicsElement ) : { x : number; y : number; width : number; height : number } | null {
    if (!this.svgRoot) {
      return null;
    }

    const bbox = this.getBBoxSafe(element);

    if (!bbox || !bbox.width || !bbox.height) {
      return null;
    }

    const elementScreenCTM = element.getScreenCTM();
    const rootScreenCTM    = this.svgRoot.getScreenCTM();

    if (!elementScreenCTM || !rootScreenCTM) {
      return null;
    }

    const elementToRoot = rootScreenCTM.inverse().multiply(elementScreenCTM);

    const cornerSources : Array<[ number, number ]> = [
      [ bbox.x,              bbox.y ],
      [ bbox.x + bbox.width, bbox.y ],
      [ bbox.x,              bbox.y + bbox.height ],
      [ bbox.x + bbox.width, bbox.y + bbox.height ]
    ];

    const transformed = cornerSources.map(([ x, y ]) => {
      const p = this.svgRoot!.createSVGPoint();
      p.x = x;
      p.y = y;
      return p.matrixTransform(elementToRoot);
    });

    const xs   = transformed.map(p => p.x);
    const ys   = transformed.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);

    return {
      x     : minX,
      y     : minY,
      width : Math.max(...xs) - minX,
      height: Math.max(...ys) - minY
    };
  }

  private positionOverlayAtRoot (
    overlay : SVGGElement | null,
    rootBox : { x : number; y : number; width : number; height : number },
    animate : boolean
  ) : void {
    if (!overlay || !this.svgRoot) {
      return;
    }

    const rect = overlay.querySelector('rect');

    if (!rect) {
      return;
    }

    const padding = 6;
    const targetX = rootBox.x - padding;
    const targetY = rootBox.y - padding;
    const targetW = rootBox.width  + padding * 2;
    const targetH = rootBox.height + padding * 2;

    // Ensure overlay is parked at svgRoot and at the end of children so it paints on top of components.
    if (overlay.parentNode !== this.svgRoot || overlay !== this.svgRoot.lastElementChild) {
      this.svgRoot.appendChild(overlay);
    }

    overlay.style.opacity = '1';

    if (animate) {
      this.animateRectTo(rect, targetX, targetY, targetW, targetH);
    } else {
      if (overlay === this.highlightGroup) {
        this.cancelHighlightAnimation();
      }
      rect.setAttribute('x',      String(targetX));
      rect.setAttribute('y',      String(targetY));
      rect.setAttribute('width',  String(targetW));
      rect.setAttribute('height', String(targetH));
    }
  }

  private animateRectTo (
    rect    : Element,
    targetX : number,
    targetY : number,
    targetW : number,
    targetH : number
  ) : void {
    this.cancelHighlightAnimation();

    const startX = parseFloat(rect.getAttribute('x')      || '0');
    const startY = parseFloat(rect.getAttribute('y')      || '0');
    const startW = parseFloat(rect.getAttribute('width')  || '0');
    const startH = parseFloat(rect.getAttribute('height') || '0');

    if (!startW || !startH) {
      rect.setAttribute('x',      String(targetX));
      rect.setAttribute('y',      String(targetY));
      rect.setAttribute('width',  String(targetW));
      rect.setAttribute('height', String(targetH));
      return;
    }

    const duration = this.HIGHLIGHT_GLIDE_MS;
    const start    = performance.now();
    const easeOut  = ( t : number ) : number => 1 - Math.pow(1 - t, 3);

    const tick = () : void => {
      const elapsed = performance.now() - start;
      const t       = Math.min(elapsed / duration, 1);
      const k       = easeOut(t);

      rect.setAttribute('x',      String(startX + ( targetX - startX ) * k));
      rect.setAttribute('y',      String(startY + ( targetY - startY ) * k));
      rect.setAttribute('width',  String(startW + ( targetW - startW ) * k));
      rect.setAttribute('height', String(startH + ( targetH - startH ) * k));

      if (t < 1) {
        this.highlightRafId = requestAnimationFrame(tick);
      } else {
        this.highlightRafId = null;
      }
    };

    this.zone.runOutsideAngular(() => {
      this.highlightRafId = requestAnimationFrame(tick);
    });
  }

  private cancelHighlightAnimation () : void {
    if (this.highlightRafId !== null) {
      cancelAnimationFrame(this.highlightRafId);
      this.highlightRafId = null;
    }
  }

  private getBBoxSafe ( element : SVGGraphicsElement ) : SVGRect | null {
    try {
      const bbox = element.getBBox();
      return ( !bbox || (!bbox.width && !bbox.height) ) ? null : bbox;
    } catch {
      return null;
    }
  }

  // ── Stage transform (applied on the outer zoom-stage wrapper) ──

  private updateStageTransform ( animate : boolean ) : void {
    const stage = this.zoomStage?.nativeElement;

    if (!stage) {
      return;
    }

    stage.classList.toggle('is-animating', animate);
    stage.style.transform = this.stageTransform;
  }

  // ── Zoom (wheel + buttons) ───

  private getNextZoomLevel ( direction : number ) : number | null {
    if (direction > 0) {
      const next = this.ZOOM_STEPS.find(step => step > this.zoomLevel);
      return next !== undefined ? next : null;
    }

    const prev = this.ZOOM_STEPS.filter(step => step < this.zoomLevel);
    return prev.length ? prev[prev.length - 1] : null;
  }

  private zoomToPoint ( newZoom : number, focalX ?: number, focalY ?: number ) : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport) {
      this.zoomLevel = newZoom;
      return;
    }

    const oldZoom = this.zoomLevel;

    const fx = focalX !== undefined ? focalX : viewport.clientWidth  / 2;
    const fy = focalY !== undefined ? focalY : viewport.clientHeight / 2;

    // Keep the content point under the focal position stationary
    this.panX = fx - ( fx - this.panX ) * newZoom / oldZoom;
    this.panY = fy - ( fy - this.panY ) * newZoom / oldZoom;

    this.zoomLevel = newZoom;
    this.updateStageTransform(true);
  }

  private bindWheelZoom () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport || this.wheelListenerBound) {
      return;
    }

    this.wheelListenerBound = ( event : WheelEvent ) => {
      if (event.ctrlKey) {
        return;
      }

      event.preventDefault();

      const direction = event.deltaY < 0 ? 1 : -1;
      const next      = this.getNextZoomLevel(direction);

      if (next !== null) {
        const rect   = viewport.getBoundingClientRect();
        const focalX = event.clientX - rect.left;
        const focalY = event.clientY - rect.top;

        this.zoomToPoint(next, focalX, focalY);
      }
    };

    viewport.addEventListener('wheel', this.wheelListenerBound, { passive: false });
  }

  private unbindWheelZoom () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (viewport && this.wheelListenerBound) {
      viewport.removeEventListener('wheel', this.wheelListenerBound);
      this.wheelListenerBound = undefined;
    }
  }

  // ── Click-drag-pan ──

  private bindDragPan () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport || this.dragMouseDownBound) {
      return;
    }

    this.dragMouseDownBound = ( event : MouseEvent ) => {
      if (event.button !== 0) {
        return;
      }

      this.isDragging    = false;
      this.dragStartX    = event.clientX;
      this.dragStartY    = event.clientY;
      this.dragStartPanX = this.panX;
      this.dragStartPanY = this.panY;

      document.addEventListener('mousemove', this.dragMouseMoveBound!);
      document.addEventListener('mouseup',   this.dragMouseUpBound!);
    };

    this.dragMouseMoveBound = ( event : MouseEvent ) => {
      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;

      if (!this.isDragging && (Math.abs(dx) > this.DRAG_THRESHOLD || Math.abs(dy) > this.DRAG_THRESHOLD)) {
        this.isDragging       = true;
        viewport.style.cursor = 'grabbing';
      }

      if (this.isDragging) {
        event.preventDefault();
        this.dragLatestX = event.clientX;
        this.dragLatestY = event.clientY;

        if (this.dragRafId === null) {
          this.dragRafId = requestAnimationFrame(() => {
            this.panX = this.dragStartPanX + ( this.dragLatestX - this.dragStartX );
            this.panY = this.dragStartPanY + ( this.dragLatestY - this.dragStartY );

            this.updateStageTransform(false);

            this.dragRafId = null;
          });
        }
      }
    };

    this.dragMouseUpBound = ( event : MouseEvent ) => {
      if (this.isDragging) {
        // Suppress the click that would otherwise fire on SVG components
        event.preventDefault();
        event.stopPropagation();
      }

      this.isDragging       = false;
      viewport.style.cursor = '';

      if (this.dragRafId !== null) {
        cancelAnimationFrame(this.dragRafId);
        this.dragRafId = null;
      }

      document.removeEventListener('mousemove', this.dragMouseMoveBound!);
      document.removeEventListener('mouseup',   this.dragMouseUpBound!);
    };

    viewport.addEventListener('mousedown', this.dragMouseDownBound);
  }

  private unbindDragPan () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (viewport && this.dragMouseDownBound) {
      viewport.removeEventListener('mousedown', this.dragMouseDownBound);
      this.dragMouseDownBound = undefined;
    }

    if (this.dragMouseMoveBound) {
      document.removeEventListener('mousemove', this.dragMouseMoveBound);
      this.dragMouseMoveBound = undefined;
    }

    if (this.dragMouseUpBound) {
      document.removeEventListener('mouseup', this.dragMouseUpBound);
      this.dragMouseUpBound = undefined;
    }

    if (this.dragRafId !== null) {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = null;
    }
  }

  // ── SVG event handlers (hover/click on components in the SVG) ──

  private attachSvgHandlers () : void {
    this.detachSvgHandlers();

    if (!this.svgRoot) {
      return;
    }

    const group = document.createElementNS(this.SVG_NS, 'g');
    group.setAttribute('class', 'cwd-hit-areas');

    this.componentBoxes.forEach(( box, code ) => {
      const rect = document.createElementNS(this.SVG_NS, 'rect') as SVGRectElement & { __cwdCode? : string };

      rect.setAttribute('class',          'cwd-hit-area');
      rect.setAttribute('x',              String(box.x));
      rect.setAttribute('y',              String(box.y));
      rect.setAttribute('width',          String(box.width));
      rect.setAttribute('height',         String(box.height));
      rect.setAttribute('fill',           'transparent');
      rect.setAttribute('pointer-events', 'all');
      rect.style.cursor = 'pointer';
      rect.__cwdCode    = code;

      rect.addEventListener('mouseenter', this.onSvgEnter);
      rect.addEventListener('mouseleave', this.onSvgLeave);
      rect.addEventListener('click',      this.onSvgClick);

      group.appendChild(rect);
      this.boundSvgTargets.push(rect as unknown as SVGGraphicsElement);
    });

    this.svgRoot.appendChild(group);
    this.hitAreaGroup = group;
  }

  private detachSvgHandlers () : void {
    for (const target of this.boundSvgTargets) {
      target.removeEventListener('mouseenter', this.onSvgEnter);
      target.removeEventListener('mouseleave', this.onSvgLeave);
      target.removeEventListener('click',      this.onSvgClick);
    }
    this.boundSvgTargets = [];

    if (this.hitAreaGroup && this.hitAreaGroup.parentNode) {
      this.hitAreaGroup.parentNode.removeChild(this.hitAreaGroup);
    }
    this.hitAreaGroup = null;
  }

  private onSvgEnter = ( event : Event ) => {
    const target = event.currentTarget as SVGGraphicsElement & { __cwdCode? : string };
    const code   = target?.__cwdCode || '';

    this.clearLeaveDebounceTimer();

    this.zone.run(() => {
      this.hoveredCode = code;
      this.applyHoverClass();
    });
  };

  private onSvgLeave = () => {
    // Defer clearing the hover — if the pointer is crossing from one bound target to another bound to the SAME component code
    this.clearLeaveDebounceTimer();

    this.leaveDebounceTimer = window.setTimeout(() => {
      this.leaveDebounceTimer = null;
      this.zone.run(() => {
        this.hoveredCode = '';
        this.applyHoverClass();
      });
    }, this.HOVER_LEAVE_DELAY_MS);
  };

  private clearLeaveDebounceTimer () : void {
    if (this.leaveDebounceTimer !== null) {
      window.clearTimeout(this.leaveDebounceTimer);
      this.leaveDebounceTimer = null;
    }
  }

  private onSvgClick = ( event : Event ) => {
    event.stopPropagation();

    const target = event.currentTarget as SVGGraphicsElement & { __cwdCode? : string };
    const code   = target?.__cwdCode;

    if (!code || code === this.selectedCode) {
      return;
    }

    this.zone.run(() => {
      this.selectedCode = code;
      this.focusComponent(code, true);
    });
  };

  private applyHoverClass () : void {
    const hoveredCode = this.hoveredCode;
    const skip        = !hoveredCode || hoveredCode === this.selectedCode;
    const rootBox     = skip ? null : this.componentBoxes.get(hoveredCode);

    if (!rootBox) {
      this.hideHoverHighlight();
      return;
    }

    this.positionOverlayAtRoot(this.hoverHighlightGroup, rootBox, false);
  }

  private hideHoverHighlight () : void {
    if (this.hoverHighlightGroup) {
      this.hoverHighlightGroup.style.opacity = '0';
    }
  }
}
